import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { 
  Search, ExternalLink, FileText, Clock, Link2, Loader2, Sparkles
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ProjectResearch = ({ projectId }) => {
  const [research, setResearch] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedResearch, setSelectedResearch] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchResearch();
  }, [projectId]);

  const fetchResearch = async () => {
    try {
      const response = await axios.get(`${API}/ai-research/by-project/${projectId}`);
      setResearch(response.data);
    } catch (error) {
      console.error('Failed to fetch research:', error);
    } finally {
      setLoading(false);
    }
  };

  // Parse inline links from summary
  const parseInlineLinks = (text) => {
    if (!text) return [];
    const linkPattern = /\[\[([^\]]+)\]\]\(([^)]+)\)/g;
    const parts = [];
    let lastIndex = 0;
    let match;
    
    while ((match = linkPattern.exec(text)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ type: 'text', content: text.slice(lastIndex, match.index) });
      }
      parts.push({ type: 'link', title: match[1], url: match[2] });
      lastIndex = match.index + match[0].length;
    }
    
    if (lastIndex < text.length) {
      parts.push({ type: 'text', content: text.slice(lastIndex) });
    }
    
    return parts.length > 0 ? parts : [{ type: 'text', content: text }];
  };

  if (loading) {
    return (
      <Card>
        <CardHeader className="py-3 px-4">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Search className="h-4 w-4" />
            Recherchen
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-3">
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader className="py-3 px-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Search className="h-4 w-4" />
              Recherchen
              {research.length > 0 && (
                <Badge variant="secondary" className="text-xs ml-1">
                  {research.length}
                </Badge>
              )}
            </CardTitle>
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-7 text-xs"
              onClick={() => navigate('/research')}
            >
              <Sparkles className="h-3 w-3 mr-1" />
              Neu
            </Button>
          </div>
        </CardHeader>
        <CardContent className="px-4 pb-3">
          {research.length === 0 ? (
            <div className="text-center py-4">
              <Search className="h-8 w-8 mx-auto mb-2 text-muted-foreground/50" />
              <p className="text-xs text-muted-foreground">
                Keine Recherchen zugeordnet
              </p>
              <Button 
                variant="link" 
                size="sm" 
                className="text-xs mt-1"
                onClick={() => navigate('/research')}
              >
                Recherche starten →
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {research.slice(0, 5).map((item) => (
                <div
                  key={item.id}
                  className="p-2 border rounded-lg hover:bg-muted/50 cursor-pointer transition-colors"
                  onClick={() => setSelectedResearch(item)}
                >
                  <div className="flex items-start gap-2">
                    <FileText className="h-3.5 w-3.5 mt-0.5 text-primary shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium truncate">{item.title}</p>
                      <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
                        {item.summary?.substring(0, 80)}...
                      </p>
                      <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                        <Clock className="h-3 w-3" />
                        {new Date(item.created_at).toLocaleDateString('de-CH')}
                        <span>•</span>
                        <span>{item.sources?.filter(s => s.url).length || 0} Quellen</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              {research.length > 5 && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="w-full text-xs"
                  onClick={() => navigate('/research')}
                >
                  Alle {research.length} Recherchen anzeigen →
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Research Detail Dialog */}
      <Dialog open={!!selectedResearch} onOpenChange={() => setSelectedResearch(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              {selectedResearch?.title}
            </DialogTitle>
            <DialogDescription>
              Recherchiert am {selectedResearch && new Date(selectedResearch.created_at).toLocaleString('de-CH')}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Summary with inline links */}
            <div>
              <h4 className="font-semibold mb-2 text-sm flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Zusammenfassung
              </h4>
              <div className="text-sm leading-relaxed">
                {parseInlineLinks(selectedResearch?.summary).map((part, idx) => {
                  if (part.type === 'link') {
                    return (
                      <a
                        key={idx}
                        href={part.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 mx-0.5 rounded bg-blue-50 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-500/30 text-xs font-medium transition-colors"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Link2 className="h-3 w-3" />
                        {part.title}
                      </a>
                    );
                  }
                  return part.content.split('\n').map((line, lineIdx, arr) => (
                    <React.Fragment key={`${idx}-${lineIdx}`}>
                      {line}
                      {lineIdx < arr.length - 1 && <br />}
                    </React.Fragment>
                  ));
                })}
              </div>
            </div>

            {/* Sources */}
            {selectedResearch?.sources?.filter(s => s.url).length > 0 && (
              <div>
                <h4 className="font-semibold mb-2 text-sm">
                  Quellen ({selectedResearch.sources.filter(s => s.url).length})
                </h4>
                <div className="space-y-1">
                  {selectedResearch.sources
                    .filter(s => s.url && s.type !== 'summary')
                    .slice(0, 8)
                    .map((source, idx) => (
                    <a
                      key={idx}
                      href={source.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 p-2 border rounded hover:bg-muted/50 transition-colors text-xs"
                    >
                      <ExternalLink className="h-3 w-3 text-primary shrink-0" />
                      <span className="font-medium">{source.title}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ProjectResearch;
